/**
 * Why an edit was refused, as the bar says it: the editing model's own
 * reasons, from a kind that does not fit to a number out of range. A name in
 * one is the page's own, as written.
 */
export const refusals = {
  // ---- parts that are not there (an app's code asked for them by id)
  noElement: (id: string) => `There is no element "${id}"`,
  notARole: (role: string) => `“${role}” is not a role’s name: letters, digits, dots and underscores, with ! before it to hide the part from people holding it`,
  noElementOnSheet: (id: string) => `There is no element "${id}" on the sheet`,
  noStepOrSection: (id: string) => `There is no step or section "${id}"`,
  noQuestion: (id: string) => `There is no question "${id}"`,
  noField: (id: string) => `There is no field "${id}"`,
  noSection: (id: string) => `There is no section "${id}"`,
  noTab: (id: string) => `There is no tab "${id}"`,
  noTabs: (id: string) => `There are no tabs "${id}"`,
  noVersion: (version: number) => `There is no version ${version}`,
  noLook: (id: string) => `There is no look “${id}”`,
  noModelField: (name: string) => `The model has no field "${name}"`,
  noOption: (n: number) => `There is no option ${n}`,
  noOptions: (id: string) => `"${id}" has no options`,
  noHeaderPart: (id: string) => `There is no button, counter or badge "${id}"`,
  noFilter: (id: string) => `There is no filter "${id}"`,
  noButton: (id: string) => `There is no button "${id}"`,
  notAColumn: (name: string) => `"${name}" is not a column`,
  /** An edit that would leave the page broken: where, and what is wrong there. */
  invalid: (path: string, message: string) => `${path}: ${message}`,
  /** Words whose braces name no field (`{customer}`): whose words, and the name; the fix shows the braces as written. */
  braceNoField: (label: string, name: string) => `${label}’s words name {${name}}, which is no field`,
  braceNoFieldHere: (name: string) => `These words name {${name}}, which is no field`,
  cannotPublish: 'This page has problems and cannot be published',
  // ---- the page and its parts
  surveyHasNoRecords: 'A survey has no records to link to or list: this kind is for app screens',
  logoAddress: 'A logo is an image’s address: https://…, a path on your app, or data:image/…',
  botCheckForResponses: 'A bot check is for a form anyone may send, which collects responses: an app’s screen is behind its sign-in',
  recordsBoth: 'Records are named by two words: one for one record and one for more, such as customer and customers',
  duplicatesForRecords: 'Possible duplicates are found among a model’s records, as one is typed on its own page: a form of responses, or a list, keeps none to compare it with',
  suggestForForms: 'Fill from text fills a form’s fields: a list of records has none to fill',
  onThePageAlready: (label: string) => `${label} is on the page already`,
  optionsFromModel: (label: string) => `The options of ${label} come from the model`,
  cannotMoveFurther: 'It cannot move further',
  oneStepOrSection: 'A page needs at least one step or section',
  nothingToAddTo: 'This page has no steps or sections to add to',
  sheetAlready: 'It is a sheet already',
  sectionsAlready: 'It is a screen of sections already',
  onlySectionsBecomeSheet: 'Only a screen of sections becomes a sheet',
  onlySheetBecomesSections: 'Only a sheet becomes a screen of sections',
  takeTabsOut: 'Take the tabs out first: a screen of sections has none',
  /** The parts of a sheet's header a screen of sections cannot show, by their names in code. */
  cannotShowSheetParts: (parts: string) => `A screen of sections cannot show this sheet's ${parts}`,
  sectionForTitle: 'A screen of sections needs a section for the title field',
  onlySheetHasTitle: 'Only a sheet has a title',
  titleNeedsSection: 'The title field needs a section to go back to',
  titleIsText: 'A title is a text field',
  tabsOnSheet: 'Tabs go on a sheet',
  foldsByTitle: (title: string) => `“${title}” folds by its title: set Folds to No to take the title away`,
  questionOwnAnswer: 'A question cannot depend on its own answer',
  fieldOwnAnswer: 'A field cannot depend on its own answer',
  alwaysRequired: (label: string) => `The model requires ${label} always, so it cannot be required only sometimes`,
  arrangementMismatch: 'The arrangement does not match the fields in this section',
  // ---- a field's settings
  onlyLinksPoint: 'Only a link, links or a table of lines point to records',
  pointsFromModel: (label: string) => `What ${label} points to comes from the model`,
  sayWhichRecords: 'Say which records it points to, such as contact',
  onlyAmountCurrency: 'Only an amount has a currency',
  currencyFromModel: (label: string) => `The currency of ${label} comes from the model`,
  currencyLetters: 'A currency is three letters, such as USD',
  onlyRange: 'Only a rating, a scale or a progress has a range',
  rangeFromModel: (label: string) => `The range of ${label} comes from the model`,
  rangeWhole: 'A range runs from a smaller whole number to a bigger one',
  onlyOther: 'Only multiple choice and checkboxes take an answer of one’s own',
  otherFromModel: (label: string) => `Whether ${label} takes an answer of its own comes from the model`,
  onlyFiles: 'Only a file upload takes files',
  filesFromModel: (label: string) => `The files ${label} takes come from the model`,
  imageOnly: 'An image takes images only',
  photoSetSeveral: 'A photo set holds several photos',
  largestFile: 'The largest file is a size in bytes, more than nothing',
  smallerPhotos: 'Photos are made smaller to a whole number of pixels from 160 to 16384, at a quality from 0.3 to 1',
  severalFirst: 'Turn on More than one file first',
  atLeastFiles: 'At least is a whole number of files',
  atMostFiles: 'At most is a whole number of files, one or more',
  leastOverMost: 'At least cannot be more than at most',
  onlyLinesColumns: 'Only a table of lines has columns',
  columnsFromModel: (label: string) => `The columns of ${label} come from the model`,
  linesNeedColumn: 'A table of lines needs a column',
  newColumnKind: 'A new column is text, a number, a date, yes or no, or a choice',
  notAChoiceColumn: (label: string) => `${label} is not a choice column`,
  columnNeedsOption: (label: string) => `${label} needs an option`,
  onlyChoiceFromList: 'Only a choice takes its options from a list',
  carriedFromChoice: 'Options are carried from another choice of the page',
  onlyChoiceOptionWhen: 'Only a choice’s options show by a condition',
  choicesFromModel: (label: string) => `The choices of ${label} come from the model`,
  // ---- choices
  orderFromModel: (label: string) => `The order of the options of ${label} comes from the model`,
  theOptionsFromModel: (label: string) => `The options of ${label} comes from the model`,
  picturesFromModel: (label: string) => `The pictures of ${label} comes from the model`,
  ownAnswersFromModel: (label: string) => `Whether people add their own of ${label} comes from the model`,
  columnsTakenFromModel: (label: string) => `How the columns are taken of ${label} comes from the model`,
  onlyOptions: 'Only a question with options has these',
  optionMoves: 'An option moves among the others',
  onlyCheckboxesNone: 'Only checkboxes take “None of these”',
  goesAloneAlready: (label: string) => `${label} has an option that goes alone already`,
  pictureTooBig: (size: string) => `A picture is 5 MB at most: this one is ${size}`,
  notAPicture: 'That file is not a picture',
  onlyTagsOwn: 'Only tags take answers of one’s own',
  onlyMatrixOnce: 'Only a matrix has columns to take once',
  onlyYesNoLook: 'Only a yes or no shows as buttons or a switch',
  onlyAlertButtons: 'Only an alert has buttons inside it',
  onlyRibbonAlertField: 'Only a ribbon or an alert takes its words from a field',
  onlyRibbonTooltip: 'Only a ribbon has words shown on pointing at it',
  onlyAlertCloses: 'Only an alert can be closed',
  onlySomeIcons: 'A ribbon or an alert has no icon',
  onlyOneChoiceBadge: 'Only a field that holds one choice of a list shows as a coloured badge',
  onlyLinksTree: 'Only a field that links to several records shows as a tree to tick',
  onlyLinkPath: 'Only a field that links to one record shows as a path picker',
  onlyStateDotColours: 'Only a field shown as a state’s dot takes a colour for each state',
  colourAsHex: 'A colour is written as #rrggbb, such as #007acc',
  timesFromJson: (label: string) => `The time per step comes from a field holding JSON; ${label} does not`,
  foldStages: 'Only stages a link points to fold: a choice keeps the steps it shows',
  savesOnClick: 'A click saves the record only where a step can be clicked',
  hotkeyShape: 'A key is one letter or digit, with Shift or without: V, Shift+G',
  hotkeyBrowser: (key: string) => `${key} is the browser’s own: pick another key`,
  onlyButtonKey: 'Only a button has a key',
  alwaysHidden: 'It is hidden always: show it first, then say when',
  titleLabelNeedsTitle: 'Words over the title need a title field first',
  megabytes: (n: number) => `${n} MB`,
  // ---- kinds' own settings
  matrixNoPictures: 'A matrix’s columns have no pictures',
  onlyOptionsPictures: 'Only a question with options has pictures and points',
  pointsNumber: 'Points are a number, such as 1 or 0.5',
  looksForChoices: 'A second line, an icon and a colour are for a choice’s options: a matrix’s columns take none',
  colourHex: 'A colour is #rrggbb, such as #0b5cad',
  seatsNumber: 'Places are a whole number, 0 or more',
  seatsForChoices: 'Places are for a choice’s options: a matrix’s columns take none',
  templatesForRecords: 'Templates are kept of a record: a form that collects responses has none',
  limitsForResponses: 'Open and close dates and limits are for a form that collects responses: an app’s own screen opens and closes with the app',
  closesBeforeOpens: 'It would close before it opens: pick a closing time after the opening',
  approvalForRecords: 'Approvals are asked of a model’s records, on a record’s own screen: a form of responses, or a list, has no record to approve',
  quizForResponses: 'A quiz’s results are for a form that collects responses',
  rightAnswersForChoices: 'Right answers are a choice’s options: a matrix’s columns take none',
  answersForTyped: 'Answers to take are for a question typed: a word or a number',
  answersNumbers: 'A number’s answers are numbers, such as 20 or 2.5',
  reviewForResponses: 'A review before sending is for a form that collects responses: a record is saved as it is changed',
  flowForResponses: 'One question at a time is for a form that collects responses, in sections or pages',
  drawForResponses: 'An order drawn for each person is for a form that collects responses, and its pages',
  drawCount: (n: number) => `Ask a whole number of its questions, from 1 to ${n}`,
  timeForResponses: 'A time limit is for a form that collects responses',
  timeMinutes: 'Minutes are a number more than 0, such as 20 or 1.5',
  timeWarnBefore: 'It warns before the end: fewer minutes than the limit',
  prefillParam: (param: string) => (param ? `“${param}” is no link’s parameter: letters, digits and _ . ~ - only, at most 100` : 'Name the link’s parameter first'),
  outboxOnList: 'An outbox keeps a form’s sends made without signal: a list of records sends none',
  paymentOnList: 'A payment is taken as a form’s answers are sent: a list of records sends none',
  paymentAmount: 'An amount above 0',
  paymentCurrency: (currency: string) => `“${currency}” is no currency’s code (three capitals: EGP) nor a field of the page`,
  prefillOnList: 'A link fills the answers of a record or a response as it starts: a list of records has none',
  rowsFromModel: (label: string) => `The rows of ${label} come from the model`,
  matrixColumnsFromModel: (label: string) => `The columns of ${label} come from the model`,
  onlyMatrix: 'Only a matrix has rows and columns',
  matrixNeedsRow: 'A matrix needs a row',
  matrixNeedsColumn: 'A matrix needs a column',
  onlyAddressParts: 'Only an address has parts',
  addressOnePart: 'An address asks for one part at least',
  answersFromModel: (label: string) => `How many answers ${label} takes comes from the model`,
  onlySeveral: 'Only pictures to choose from and a matrix take one answer or several',
  // ---- limits
  fromToNumbers: 'From and To are numbers',
  wholeRange: 'A whole number runs from and to whole numbers',
  dayIs: 'A day is a date, today, or days after or before today',
  limitsFromModel: (label: string) => `The limits of ${label} come from the model`,
  // ---- a text in several languages
  onlyTextLanguages: 'Only a short answer, a paragraph or formatted text is typed in several languages',
  languagesFromModel: (label: string) => `The languages of ${label} come from the model`,
  languageTag: (tag: string) => `“${tag}” is not a language’s code: write it as ar, en or pt-BR`,
  languagesPlainText: 'A text in several languages is typed as words: show it as a short answer, a paragraph or formatted text first',
  requiredInNeedsLanguages: 'Choose the languages it is typed in first',
  requiredInOne: 'A required answer is typed in one language at least: to ask for none, take Required off',
  onlyTextSize: 'Only a short answer or a paragraph has a most characters',
  sizeWhole: 'The most characters is a whole number, 1 or more',
  onlyLimits: 'Only a number, an amount or a date has limits',
  earliestAfterLatest: 'The earliest day is after the latest',
  fromNotLess: 'From is not less than To',
  onlyDecimals: 'Only a number or an amount has decimals',
  decimalsRange: 'Decimals run from 0 to 6',
  onlyDateDays: 'Only a date falls on days of the week',
  onlyDateTimeZone: 'Only a date and time is on a time zone’s clock',
  zoneUnknown: (zone: string) => `“${zone}” is no time zone: name one as the IANA does, such as Africa/Cairo or Europe/Berlin`,
  daysRange: 'The days of the week are 1, Monday, to 7, Sunday',
  oneDay: 'A date falls on one day of the week at least',
  onlyDateKeeps: 'Only a date keeps a month or a year alone, or shows the Hijri calendar',
  hijriIsADay: 'A Hijri date is a day: keep a day to show the Hijri calendar beside it',
  onlyDateToday: 'Only a date starts on today',
  onlyScaleNps: 'Only a linear scale is made NPS',
  onlySliderStep: 'Only a slider has a step',
  stepNumber: 'A step is a number more than 0, such as 1 or 0.5',
  stepWhole: (label: string) => `${label} holds whole numbers in the model, so it steps by whole numbers`,
  // ---- structures
  onlyLinesTotals: 'Only a table of lines has totals and columns to hide',
  onlyNumbersAdd: 'Only a column of numbers adds up',
  onlyLinksOffer: 'Only a link or links offer records',
  offersFromModel: (label: string) => `The records ${label} offers come from the model`,
  sayWhichFields: 'Say which of their fields, such as active',
  // ---- a sheet's header
  onlySheetHeader: 'Only a record sheet has a header',
  statusOneOfList: (label: string, stored: string) => `Status steps show a field that holds one of a list; ${label} holds ${stored}`,
  headerNeedsWords: 'A button, a counter or a badge needs words',
  badgeNoAction: 'A badge has no action',
  counterAction: 'A counter needs the name of its action, such as open_invoices',
  buttonAction: 'A button needs the name of its action, such as confirm',
  onlyButtonStyle: 'Only a button has a style',
  onlyButtonAsks: 'Only a button asks before it acts',
  onlyBadgeTone: 'Only a badge has a tone',
  onlyCounterField: 'Only a counter shows a field',
  counterNumber: (label: string, stored: string) => `A counter shows a number, an amount, a date or words; ${label} holds ${stored}`,
  onlyCounterSecond: 'Only a counter shows a second value, a unit or words from a field under its value',
  secondWordsNeedValue: 'Words for a second value need the second value first',
  // ---- around a record: the gear menu, the attachment beside it, the side panel
  onlyMenuBuiltin: 'Only an item of the gear menu archives, duplicates or deletes the record itself',
  onlyMenuGroup: 'Only an item of the gear menu goes under Print',
  menuNeedsWords: 'An item of the gear menu needs words, unless it is one of the record’s own (Archive, Duplicate, Delete)',
  dividerIsALine: 'A divider is a line between items: it has no words and does nothing',
  openerNeedsWords: 'An item that opens a menu needs words',
  openerDoesNothing: 'An item that opens a menu does nothing of its own: each item in its menu does',
  openerNeedsItems: 'An item that opens a menu needs an item in it, or the app’s action that gives its items',
  onlyOpenerItems: 'Only an item that opens a menu has items of its own: add one from Add to the gear menu',
  previewNeedsFile: (label: string, stored: string) => `The attachment beside the sheet comes from a file or a picture; ${label} holds ${stored}`,
  noSidePanel: 'The sheet has no side panel to keep beside it',
  // ---- a list
  onlyListColumns: 'Only a list has columns',
  columnAlready: (label: string) => `${label} is a column already`,
  cannotBeColumn: (label: string, stored: string) => `A list cannot show ${label} in a column: it holds ${stored}`,
  listNeedsColumn: 'A list needs a column',
  pageSize: 'A page of the list holds 1 to 500 rows',
  filterName: 'A filter needs a name people will recognise, such as Active',
  filterCondition: 'A filter needs a condition',
  buttonWords: 'A button needs words',
  listButtonAction: 'A button needs the name of its action, such as archive',
  cellWidth: 'A column is 1 to 200 characters wide, or 8 to 4000 pixels',
  cellRule: 'A header’s rule is set in 0 to 400 pixels from the column’s end',
  filterLabelNotOffered: (label: string) => `The filter bar does not offer ${label}: tick it first`,
  leadNeedsChoice: (label: string) => `An icon before the words comes from a choice’s value; ${label} is not a choice`,
  dotNeedsChoice: (label: string) => `A state’s dot is for a choice; ${label} is not one`,
  cellWidgetKind: (label: string) => `${label} cannot be drawn so: chips are a link’s records, a bar is a number’s, a phone is a text’s`,
  periodNeedsDate: (label: string) => `A period is a date’s; ${label} holds no date`,
  totalNeedsNumber: (label: string) => `A sum, an average, the lowest and the highest are a number’s; ${label} is not one. Any column can count its records`,
  parentNotOwnLink: (label: string) => `Rows nest under a link to the list’s own records; ${label} is not one`,
  detailNotLines: (label: string) => `A row opens to a table of lines of the page’s; ${label} is not one`,
  detailNoLineField: (label: string, name: string) => `${label} has no field “${name}” to show`,
  rankNotWhole: (label: string) => `Rows are ranked by a whole number; ${label} is not one`,
  rankUnderParents: 'Rows under their parent are not ranked by hand: a list ranks one level',
  endlessUnderParents: 'Rows under their parent come a page at a time: a list of them never loads as it is scrolled',
  childrenNeedParent: 'Children load as a parent opens in a tree: choose the link the rows nest under first',
  notFilterable: (label: string) => `The filter bar cannot list the values of ${label}, nor take its range`,
  notRangeable: (label: string) => `The filter bar takes the range of a number or a date; ${label} is neither`,
  atLeastNeedsNumber: (label: string) => `A press for a least value is a number’s; ${label} holds dates`,
  atLeastWhole: (label: string) => `${label} holds whole numbers: each least value is one`,
  atLeastNotNumbers: (label: string) => `${label}’s least values are numbers, parted by commas: 1, 2, 3`,
  sheetNeedsFilterBar: 'The sheet on a phone is the filter bar’s: choose “A keyword and each field’s values” first',
  chooseHasButtons: 'The list has buttons for the rows chosen: take them away before the box to choose a row',
  notBulkSettable: (label: string) => `Edit cannot set ${label} in many rows at once: it is read-only, worked out, or not a value one word, number, date, choice or link can give`,
  noneChangeable: 'No column of this list can be changed in a cell: words, a number, money, yes or no, a date, a choice or a link people can change',
  notImportable: (label: string) => `Import cannot fill ${label} from a file: it is read-only, worked out, or not a value a cell’s words, number, date, choice or link can give`,
};
